import React, { useState } from 'react';
import './Help.css';

const Help = () => {
  const [activeSection, setActiveSection] = useState('getting-started');

  const sections = [
    { id: 'getting-started', title: 'Getting Started' },
    { id: 'profile-settings', title: 'Profile Settings' },
    { id: 'search', title: 'Search' },
    { id: 'community-feed', title: 'Feed' },
    { id: 'my-connections', title: 'My Connections' },
    { id: 'user-levels', title: 'User Levels' },
    { id: 'faq', title: 'FAQ' },
    { id: 'contact', title: 'Contact Support' }
  ];

  const renderContent = () => {
    switch (activeSection) {
      case 'getting-started':
        return (
          <div className="help-content-section">
            <h2>Getting Started</h2>
            <div className="help-page-card">
              <h3>Welcome to CofounderHubs</h3>
              <p>
                CofounderHubs helps startups and entrepreneurs discover suitable co-founders,
                candidates, investors, and business opportunities in one platform.
                Whether you are looking for a co-founder, hiring talent, posting a job, or
                exploring investor connections, this is where the startup ecosystem comes together.
              </p>
            </div>
            <div className="help-page-card">
              <h3>Getting Started in 3 Steps</h3>
              <ul style={{ textAlign: 'left', marginTop: '10px' }}>
                <li><strong>Create Your Profile</strong> - Sign up and complete your profile with your skills, experience, and what you're looking for.</li>
                <li><strong>Explore & Connect</strong> - Browse co-founders, talent, and opportunities using search filters, and send connection requests.</li>
                <li><strong>Build & Hire</strong> - Start conversations, post jobs when needed, and grow your startup with the right people.</li>
              </ul>
            </div>
            <div className="help-page-card">
              <h3>Key Features</h3>
              <ul style={{ textAlign: 'left', marginTop: '10px' }}>
                <li><strong>Co-founder Discovery</strong> - Search and connect with potential co-founders</li>
                <li><strong>Community Feed</strong> - Share ideas and connect with entrepreneurs</li>
                <li><strong>Talent & Job Posting</strong> - Find candidates and post open roles (Basic Free, Enhanced $29, Premium $99)</li>
                <li><strong>Investor Connections</strong> - Discover potential investors and business opportunities</li>
                <li><strong>Recruitment Support</strong> - Optional help identifying and connecting with suitable candidates</li>
              </ul>
            </div>
          </div>
        );

      case 'profile-settings':
        return (
          <div className="help-content-section">
            <h2>Profile Settings</h2>
            <div className="help-page-card">
              <h3>Profile Information</h3>
              <p>
                Keep your profile up-to-date to attract the right co-founders. You can edit:
              </p>
              <ul style={{ textAlign: 'left', marginTop: '10px' }}>
                <li>Username and email address</li>
                <li>Full name and location</li>
                <li>LinkedIn profile URL</li>
                <li>Y Combinator profile URL</li>
              </ul>
            </div>
            <div className="help-page-card">
              <h3>Change Password</h3>
              <p>
                Secure your account by regularly updating your password. You'll need to
                provide your current password to set a new one.
              </p>
            </div>
            <div className="help-page-card">
              <h3>Upgrade Level</h3>
              <p>
                Your level represents how much you've contributed to the platform. To upgrade
                your level, you need to make a payment - your level equals the amount you spend.
                For example, if you spend $15, your level becomes 15.
              </p>
              <p>
                Higher levels may unlock additional features and benefits. Contact our support
                team to learn more about upgrading your level.
              </p>
            </div>
          </div>
        );

      case 'search':
        return (
          <div className="help-content-section">
            <h2>Search - Finding Cofounders</h2>
            <div className="help-page-card">
              <h3>How to Search for Co-founders</h3>
              <p>
                Use our advanced filters to find co-founders based on technical skills,
                location, interests, and what they're looking for in a partner.
              </p>
            </div>
            <div className="help-page-card">
              <h3>Search Results Ranking</h3>
              <p>
                Search results are sorted to show the most relevant candidates first.
                The ranking is determined by:
              </p>
              <ul style={{ textAlign: 'left', marginTop: '10px' }}>
                <li><strong>Level</strong> - Higher level users appear first (primary factor)</li>
                <li><strong>Last Seen</strong> - More recently active users rank higher (secondary factor)</li>
              </ul>
              <p>
                This ensures you see the most engaged and committed members at the top of your search results.
              </p>
            </div>
            <div className="help-page-card">
              <h3>Creating Your Profile</h3>
              <p>
                Sign up and complete your profile to let potential co-founders know
                about your skills, experience, and what you're looking to build.
              </p>
            </div>
          </div>
        );

      case 'community-feed':
        return (
          <div className="help-content-section">
            <h2>Community Feed</h2>
            <div className="help-page-card">
              <h3>What is the Feed?</h3>
              <p>
                The Feed is a community space where you can share your ideas, find partners,
                and connect with other entrepreneurs. Post about your startup, what you're
                looking for, or offer your expertise.
              </p>
            </div>
            <div className="help-page-card">
              <h3>Post Categories</h3>
              <ul style={{ textAlign: 'left', marginTop: '10px' }}>
                <li><strong>New Idea</strong> - For entrepreneurs with early-stage ideas looking for cofounders or collaborators.</li>
                <li><strong>Technical Co-founder</strong> - For those needing a tech partner to build and scale their product.</li>
                <li><strong>Business Co-founder</strong> - For technical founders looking for a partner with business, marketing, or operations skills.</li>
                <li><strong>Investor</strong> - For entrepreneurs looking for early-stage funding or investors interested in supporting a startup.</li>
                <li><strong>Advisor</strong> - For founders looking for mentorship or strategic advice from experienced industry professionals.</li>
                <li><strong>Partner</strong> - For those seeking partnerships with other companies or teams to collaborate on business growth and product development.</li>
                <li><strong>Early Engineer</strong> - For those seeking the first engineers to help develop and launch the product.</li>
                <li><strong>Freelancer</strong> - For founders needing short-term expertise in specific areas like design, content, marketing, etc.</li>
              </ul>
            </div>
            <div className="help-page-card">
              <h3>Posting Limits</h3>
              <p>
                To maintain quality, you can create 1 post per day. You can comment up to
                10 times per post per day, with a total limit of 50 comments per day.
              </p>
            </div>
            <div className="help-page-card">
              <h3>Notifications</h3>
              <p>
                Get notified when someone comments on your post, likes or dislikes your content.
                Check the bell icon in the header to see your notifications.
              </p>
            </div>
          </div>
        );

      case 'my-connections':
        return (
          <div className="help-content-section">
            <h2>My Connections</h2>
            <div className="help-page-card">
              <h3>Managing Your Connections</h3>
              <p>
                The My Connections page helps you organize and track your networking efforts.
                You can manage different types of connections and their status.
              </p>
            </div>
            <div className="help-page-card">
              <h3>Connection Tabs</h3>
              <ul style={{ textAlign: 'left', marginTop: '10px' }}>
                <li><strong>All</strong> - View all your connections regardless of status</li>
                <li><strong>Favorites</strong> - People you've marked as favorites for quick access</li>
                <li><strong>Requests Sent</strong> - Connection requests you've sent that are pending</li>
                <li><strong>Connected</strong> - Successfully established connections</li>
              </ul>
            </div>
            <div className="help-page-card">
              <h3>Connection Actions</h3>
              <p>
                For each connection, you can:
              </p>
              <ul style={{ textAlign: 'left', marginTop: '10px' }}>
                <li>Add to favorites (star icon)</li>
                <li>Send connection requests</li>
                <li>View LinkedIn and Y Combinator profiles</li>
                <li>See their location and technical skills</li>
              </ul>
            </div>
          </div>
        );

      case 'user-levels':
        return (
          <div className="help-content-section">
            <h2>User Levels & Search Filters</h2>
            <div className="help-page-card">
              <h3>What are User Levels?</h3>
              <p>
                Your user level determines which search filters you can access. Higher levels unlock
                more advanced filters to help you find the perfect co-founder. Your level is based on
                your engagement and contributions to the platform.
              </p>
            </div>
            <div className="help-page-card">
              <h3>Level-Based Filter Access</h3>
              <div className="help-level-list">
                <div className="help-level-item">
                  <strong>Not Logged In (Level 0)</strong>
                  <p>
                    Search by: Name, Age Range, Gender
                  </p>
                </div>
                <div className="help-level-item">
                  <strong>Level 1+</strong>
                  <p>
                    + Technical Background
                  </p>
                </div>
                <div className="help-level-item">
                  <strong>Level 25+</strong>
                  <p>
                    + Location, Timing, Has Business Idea, About Me, Impressive Accomplishment, YC Founder, 
                    Interests, Responsibilities, Preferred Co-founder Responsibilities, Already Has Co-founder
                  </p>
                </div>
                <div className="help-level-item">
                  <strong>Level 50+</strong>
                  <p>
                    + Education, Has Company, Company URL, Company Name, Potential Ideas, 
                    Co-founder Has Idea, Co-founder Technical Background, Co-founder Location, 
                    Co-founder Age Range, Fellow Alumni Importance, YC Alumni Importance, 
                    Woman Co-founder Importance, Equity
                  </p>
                </div>
                <div className="help-level-item">
                  <strong>Level 100+</strong>
                  <p>
                    + Employment, Company Description, Company Progress, Funding Status, 
                    Potential Ideas, Ideal Co-founder
                  </p>
                </div>
              </div>
            </div>
            <div className="help-page-card">
              <h3>Search Result Limits by Level</h3>
              <p>
                The number of search results you can view per page also depends on your level:
              </p>
              <ul style={{ textAlign: 'left', marginTop: '10px' }}>
                <li><strong>Not Logged In:</strong> Maximum 10 results per page</li>
                <li><strong>Level 1-24:</strong> Maximum 10 results per page</li>
                <li><strong>Level 25-49:</strong> Maximum 25 results per page</li>
                <li><strong>Level 50-99:</strong> Maximum 50 results per page</li>
                <li><strong>Level 100+:</strong> Maximum 100 results per page</li>
              </ul>
            </div>
            <div className="help-page-card">
              <h3>How to Increase Your Level</h3>
              <p>
                Your level represents your investment and engagement with the platform.
                To increase your level, visit your Profile Settings and upgrade. Your level
                will equal the amount you've invested in the platform.
              </p>
            </div>
          </div>
        );

      case 'faq':
        return (
          <div className="help-content-section">
            <h2>Frequently Asked Questions</h2>
            <div className="help-page-card">
              <h3>How do I contact a potential co-founder?</h3>
              <p>
                Click on a candidate's profile to view their contact information
                and preferred communication methods.
              </p>
            </div>
            <div className="help-page-card">
              <h3>Can I edit or delete my posts?</h3>
              <p>
                Yes! You can edit or delete your own posts at any time. Click on a post
                to view it, then use the edit or delete buttons.
              </p>
            </div>
            <div className="help-page-card">
              <h3>Can I add images to my posts?</h3>
              <p>
                Yes! You can upload images (JPG, PNG, GIF, WebP) up to 5MB when creating
                a post. Images help showcase your ideas and projects.
              </p>
            </div>
            <div className="help-page-card">
              <h3>Is the platform free to use?</h3>
              <p>
                CofounderHubs is now a paid platform for all users.
              </p>
            </div>
          </div>
        );

      case 'contact':
        return (
          <div className="help-content-section">
            <h2>Contact Support</h2>
            <div className="help-page-card help-page-contact-card">
              <p className="help-contact-intro">
                Have questions or need help? Reach out to us directly. We typically respond within 24 hours.
              </p>
              <div className="help-contact-methods">
                <a
                  href="mailto:support@cofounderhubs.com"
                  className="help-contact-button help-contact-email"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" xmlns="http://www.w3.org/2000/svg">
                    <rect x="2" y="4" width="20" height="16" rx="2" />
                    <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
                  </svg>
                  <div className="help-contact-button-content">
                    <div className="help-contact-label">Email</div>
                    <div className="help-contact-value">support@cofounderhubs.com</div>
                  </div>
                </a>
                <a
                  href="https://wa.me/17793363216"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="help-contact-button help-contact-whatsapp"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" viewBox="0 0 16 16">
                    <path d="M13.601 2.326A7.85 7.85 0 0 0 7.994 0C3.627 0 .068 3.558.064 7.926c0 1.399.366 2.76 1.057 3.965L0 16l4.204-1.102a7.9 7.9 0 0 0 3.79.965h.004c4.368 0 7.926-3.558 7.93-7.93A7.9 7.9 0 0 0 13.6 2.326zM7.994 14.521a6.6 6.6 0 0 1-3.356-.92l-.24-.144-2.494.654.666-2.433-.156-.251a6.56 6.56 0 0 1-1.007-3.505c0-3.626 2.957-6.584 6.591-6.584a6.56 6.56 0 0 1 4.66 1.931 6.56 6.56 0 0 1 1.928 4.66c-.004 3.639-2.961 6.592-6.592 6.592m3.615-4.934c-.197-.099-1.17-.578-1.353-.646-.182-.065-.315-.099-.445.099-.133.197-.513.646-.627.775-.114.133-.232.148-.43.05-.197-.1-.836-.308-1.592-.985-.59-.525-.985-1.175-1.103-1.372-.114-.198-.011-.304.088-.403.087-.088.197-.232.296-.346.1-.114.133-.198.198-.33.065-.134.034-.248-.015-.347-.05-.099-.445-1.076-.612-1.47-.16-.389-.323-.335-.445-.34-.114-.007-.247-.007-.38-.007a.73.73 0 0 0-.529.247c-.182.198-.691.677-.691 1.654s.71 1.916.81 2.049c.098.133 1.394 2.132 3.383 2.992.47.205.84.326 1.129.418.475.152.904.129 1.246.08.38-.058 1.171-.48 1.338-.943.164-.464.164-.86.114-.943-.049-.084-.182-.133-.38-.232"/>
                  </svg>
                  <div className="help-contact-button-content">
                    <div className="help-contact-label">WhatsApp</div>
                    <div className="help-contact-value">+1 (779) 336-3216</div>
                  </div>
                </a>
                <a
                  href="https://t.me/cofounderhubs"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="help-contact-button help-contact-telegram"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm4.64 6.8c-.15 1.58-.8 5.42-1.13 7.19-.14.75-.42 1-.68 1.03-.58.05-1.02-.38-1.58-.75-.88-.58-1.38-.94-2.23-1.5-.99-.65-.35-1.01.22-1.59.15-.15 2.71-2.48 2.76-2.69a.2.2 0 00-.05-.18c-.06-.05-.14-.03-.21-.02-.09.02-1.49.95-4.22 2.79-.4.27-.76.41-1.08.4-.36-.01-1.04-.2-1.55-.37-.63-.2-1.12-.31-1.08-.66.02-.18.27-.36.74-.55 2.92-1.27 4.86-2.11 5.83-2.51 2.78-1.16 3.35-1.36 3.73-1.36.08 0 .27.02.39.12.1.08.13.19.14.27-.01.06.01.24 0 .38z" />
                  </svg>
                  <div className="help-contact-button-content">
                    <div className="help-contact-label">Telegram</div>
                    <div className="help-contact-value">@cofounderhubs</div>
                  </div>
                </a>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="help-page-container">
      <div className="help-page-content">
        <div className="help-sidebar">
          <nav className="help-nav">
            {sections.map(section => (
              <button
                key={section.id}
                className={`help-nav-item ${activeSection === section.id ? 'active' : ''}`}
                onClick={() => setActiveSection(section.id)}
              >
                <span className="help-nav-title">{section.title}</span>
              </button>
            ))}
          </nav>
        </div>

        <div className="help-main-content">
          {renderContent()}
        </div>
      </div>
    </div>
  );
};

export default Help;
